import { PlusOutlined } from '@ant-design/icons'

import { Button, PageHeader } from '@lmc/ui-kit'
import { useAbilityAccess } from 'components/AccessControl'
import useResident from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { residentHealthRecordsConstants } from 'locale/residentHealthRecords'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import CommunicationRecordList from './components/CommunicationRecordList'
import CommunicationRecordModal from './components/CommunicationRecordModal'
import { useCommunicationRecordDelete } from './hooks/useCommunicationRecordDelete'
import type { CommunicationRecord } from './utils/types'

const ResidentCommunicationRecordsScreen = () => {
  const { t } = useTranslation(NAMESPACE.RESIDENT_HEALTH_RECORDS)

  const params = useParams()

  const id = params?.id || ''

  const [isCreateRecordModalOpen, setIsCreateRecordModalOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] =
    useState<CommunicationRecord | null>(null)

  const { resident, isLoading: isResidentLoading } = useResident({ id })

  const { deleteCommunicationRecord } = useCommunicationRecordDelete({})

  const pageHeaderTitle =
    isResidentLoading || !resident
      ? t(residentHealthRecordsConstants.COMMUNICATION_RECORDS)
      : `${resident?.name?.first} ${resident?.name?.last}'s ${t(residentHealthRecordsConstants.COMMUNICATION_RECORDS)}`

  const canViewCommunicationRecords = useAbilityAccess([
    {
      i: 'view',
      this: 'CommunicationRecord'
    }
  ])

  const canCreateCommunicationRecords = useAbilityAccess([
    {
      i: 'create',
      this: 'CommunicationRecord'
    }
  ])

  if (!canViewCommunicationRecords) {
    return null
  }

  const openCreateRecordModal = () => {
    setSelectedRecord(null)
    setIsCreateRecordModalOpen(true)
  }

  const handleEditRecord = (record: CommunicationRecord) => {
    setSelectedRecord(record)
    setIsCreateRecordModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsCreateRecordModalOpen(false)
    setSelectedRecord(null)
  }

  const handleDeleteRecord = (record: CommunicationRecord) => {
    const recordId = record.id || (record as any)._id
    if (recordId) {
      deleteCommunicationRecord(recordId)
    }
  }

  return (
    <>
      <PageHeader
        title={pageHeaderTitle}
        help={{
          content: t(
            residentHealthRecordsConstants.COMMUNICATION_RECORDS_TOOLTIP
          )
        }}
        sticky
      >
        {canCreateCommunicationRecords && (
          <Button onClick={openCreateRecordModal} variant="primary">
            <PlusOutlined />
            {t(residentHealthRecordsConstants.ADD_COMMUNICATION_RECORD)}
          </Button>
        )}
      </PageHeader>
      <CommunicationRecordList
        residentId={id}
        resident={resident}
        onEditRecord={handleEditRecord}
        onDeleteRecord={handleDeleteRecord}
      />
      <CommunicationRecordModal
        open={isCreateRecordModalOpen}
        onClose={handleCloseModal}
        resident={resident}
        initialRecord={selectedRecord}
      />
    </>
  )
}

export default ResidentCommunicationRecordsScreen
